import React, { useEffect, useState, useMemo } from 'react';
import { fetchAllUsers, updateSingleAppRole, bulkUpdateAppRole } from '../services/userService';
import type { AccountUser, OKRRole, EPRRole, PortalRole } from '../types';

type AppTab = 'okr' | 'epr' | 'portal';

const APP_CONFIG: Record<AppTab, { label: string; icon: string; roles: string[]; color: string }> = {
  okr: {
    label: 'OKR Tracker',
    icon:  '📊',
    roles: ['superadmin', 'admin', 'editor', 'viewer'],
    color: 'blue',
  },
  epr: {
    label: 'EPR Service',
    icon:  '📝',
    roles: ['superadmin', 'admin', 'hr_admin', 'leader', 'employee'],
    color: 'purple',
  },
  portal: {
    label: 'Leadership Portal',
    icon:  '👔',
    roles: ['superadmin', 'admin', 'viewer'],
    color: 'amber',
  },
};

const ROLE_COLORS: Record<string, string> = {
  superadmin: 'bg-red-50 text-red-700 border-red-200',
  admin:      'bg-orange-50 text-orange-700 border-orange-200',
  hr_admin:   'bg-purple-50 text-purple-700 border-purple-200',
  leader:     'bg-blue-50 text-blue-700 border-blue-200',
  employee:   'bg-gray-50 text-gray-600 border-gray-200',
  editor:     'bg-green-50 text-green-700 border-green-200',
  viewer:     'bg-gray-50 text-gray-500 border-gray-200',
};

const AppRolesPage: React.FC = () => {
  const [users, setUsers]         = useState<AccountUser[]>([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState<AppTab>('okr');
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState<Set<string>>(new Set());
  const [bulkRole, setBulkRole]   = useState('');
  const [saving, setSaving]       = useState<string | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    fetchAllUsers()
      .then(u => setUsers(u.filter(x => x.isActive !== false)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return !q ? users : users.filter(u =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, search]);

  const getRole = (user: AccountUser, app: AppTab): string =>
    user.appRoles?.[app] ?? '';

  const handleRoleChange = async (uid: string, role: string) => {
    setSaving(uid);
    try {
      await updateSingleAppRole(uid, tab, role);
      setUsers(prev => prev.map(u =>
        u.id === uid ? { ...u, appRoles: { ...u.appRoles, [tab]: role as OKRRole & EPRRole & PortalRole } } : u
      ));
    } finally {
      setSaving(null);
    }
  };

  const handleBulkApply = async () => {
    if (!bulkRole || selected.size === 0) return;
    setBulkSaving(true);
    try {
      await bulkUpdateAppRole([...selected], tab, bulkRole);
      setUsers(prev => prev.map(u =>
        selected.has(u.id) ? { ...u, appRoles: { ...u.appRoles, [tab]: bulkRole as OKRRole & EPRRole & PortalRole } } : u
      ));
      setSelected(new Set());
      setBulkRole('');
    } finally {
      setBulkSaving(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(u => u.id)));
    }
  };

  const cfg = APP_CONFIG[tab];

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">App Roles</h1>
        <p className="text-sm text-gray-500 mt-0.5">Assign per-app roles to each user</p>
      </div>

      {/* App tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {(Object.keys(APP_CONFIG) as AppTab[]).map(app => (
          <button
            key={app}
            onClick={() => { setTab(app); setSelected(new Set()); setBulkRole(''); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === app ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span>{APP_CONFIG[app].icon}</span>
            {APP_CONFIG[app].label}
          </button>
        ))}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
          <span className="text-sm font-semibold text-blue-700">{selected.size} selected</span>
          <select
            value={bulkRole}
            onChange={e => setBulkRole(e.target.value)}
            className="border border-blue-200 rounded-lg px-3 py-1.5 text-sm bg-white text-gray-700 focus:outline-none"
          >
            <option value="">— Set role —</option>
            {cfg.roles.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button
            onClick={handleBulkApply}
            disabled={!bulkRole || bulkSaving}
            className="px-4 py-1.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 transition"
            style={{ background: '#011132' }}
          >
            {bulkSaving ? 'Applying…' : 'Apply to selected'}
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto text-blue-400 hover:text-blue-600 text-sm"
          >
            Clear
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <span className="material-symbols-rounded absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: 16 }}>search</span>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search users…"
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#011132]/20"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 space-y-3 animate-pulse">
            {[1,2,3,4].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected.size === filtered.length && filtered.length > 0}
                    onChange={toggleAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Grade</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {cfg.icon} {cfg.label} Role
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(user => {
                const currentRole = getRole(user, tab);
                return (
                  <tr key={user.id} className={`transition-colors ${selected.has(user.id) ? 'bg-blue-50/60' : 'hover:bg-gray-50/60'}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(user.id)}
                        onChange={() => toggleSelect(user.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {user.photoURL || user.avatar
                          ? <img src={user.photoURL || user.avatar} className="w-7 h-7 rounded-full flex-shrink-0" alt="" />
                          : <div className="w-7 h-7 rounded-full bg-[#011132]/10 flex items-center justify-center text-[#011132] text-xs font-bold flex-shrink-0">
                              {user.name?.[0]?.toUpperCase() ?? '?'}
                            </div>
                        }
                        <div>
                          <p className="font-semibold text-gray-800">{user.name}</p>
                          <p className="text-xs text-gray-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {user.grade
                        ? <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">{user.grade}</span>
                        : <span className="text-gray-300 text-xs">—</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {currentRole && (
                          <span className={`text-xs font-semibold border rounded-full px-2 py-0.5 ${ROLE_COLORS[currentRole] ?? 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                            {currentRole}
                          </span>
                        )}
                        <select
                          value={currentRole}
                          onChange={e => handleRoleChange(user.id, e.target.value)}
                          disabled={saving === user.id}
                          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#011132]/30 disabled:opacity-50"
                        >
                          <option value="">— No access —</option>
                          {cfg.roles.map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                        {saving === user.id && (
                          <span className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin flex-shrink-0" />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AppRolesPage;
